/**
 * InfoTooltip — a small "i" affordance that opens a contextual hint.
 *
 * Every card and reward surface in Luna has one in its top-right corner.
 * The goal: a brand-new user should be able to tap any `i` and instantly
 * understand what the card is, what the numbers mean, and why they
 * matter — no external docs, no guessing.
 *
 * Visual: a 20×20 frosted pill with a lowercase "i". On tap it opens a
 * centered glass modal with a title, short body, and an optional list
 * of bullet points. Matches the same iOS 26 Liquid Glass aesthetic as
 * the rest of the app.
 *
 * Positioning is absolute within a relative parent — so the host card
 * must have `position: relative` (GlassSurface already does).
 *
 * Usage:
 *   <InfoTooltip
 *     title="Прогресс 250%"
 *     body="Каждый контракт платит максимум 250% от суммы депозита…"
 *     bullets={["Включает все бонусы", "Overflow переливается в новый слот"]}
 *   />
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Info, X } from 'lucide-react';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { cn } from '@/lib/cn';

export interface InfoTooltipProps {
  title: string;
  body: ReactNode;
  bullets?: string[];
  /** Position inside the parent — most cards use 'top-right' */
  placement?: 'top-right' | 'top-left' | 'inline';
  /** Extra class for the trigger button */
  className?: string;
  /** Small accent hue used by the icon glow */
  tone?: 'lunar' | 'plasma' | 'nebula';
  /** Label for screen readers */
  ariaLabel?: string;
}

export function InfoTooltip({
  title,
  body,
  bullets,
  placement = 'top-right',
  className,
  tone = 'lunar',
  ariaLabel,
}: InfoTooltipProps) {
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(
    (e: React.MouseEvent) => {
      // Stop propagation so tapping the "i" inside a clickable card
      // does NOT also trigger the card's own onClick.
      e.stopPropagation();
      haptic.light();
      uiSounds.bellOpen();
      setOpen(true);
    },
    [haptic, uiSounds],
  );

  const handleClose = useCallback(() => {
    uiSounds.bellClose();
    setOpen(false);
  }, [uiSounds]);

  // ESC key closes the modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const toneColor =
    tone === 'plasma'
      ? 'var(--accent-secondary)'
      : tone === 'nebula'
        ? 'var(--accent-tertiary)'
        : 'var(--accent-primary)';

  const placementClass =
    placement === 'top-right'
      ? 'absolute top-2 right-2 z-20'
      : placement === 'top-left'
        ? 'absolute top-2 left-2 z-20'
        : 'inline-flex align-middle';

  return (
    <>
      <button
        type="button"
        aria-label={ariaLabel ?? `Подсказка: ${title}`}
        onClick={handleOpen}
        className={cn(
          placementClass,
          'h-6 w-6 rounded-full flex items-center justify-center',
          'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
          'transition-all duration-200 active:scale-90',
          'cursor-pointer',
          className,
        )}
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.04) 100%)',
          border: '1px solid rgba(255,255,255,0.14)',
          boxShadow:
            'inset 0 1px 0 0 rgba(255,255,255,0.18), 0 4px 10px -4px rgba(0,0,0,0.5)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <Info size={11} strokeWidth={2} />
      </button>

      {createPortal(
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center px-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            <motion.div
              className="relative w-full max-w-sm rounded-[26px] overflow-hidden flex flex-col"
              style={{
                background:
                  'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
                border: '1px solid var(--glass-stroke-strong)',
                boxShadow:
                  'var(--glass-shadow), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
                backdropFilter: 'blur(20px) saturate(140%)',
                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                // Не выходим за высоту вьюпорта — body скроллится сам
                maxHeight: 'calc(100dvh - 80px)',
              }}
              initial={{ y: 16, opacity: 0, scale: 0.94 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 16, opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start gap-3 px-5 pt-5 pb-3 flex-shrink-0">
                <div
                  className="h-10 w-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `color-mix(in oklab, ${toneColor} 18%, transparent)`,
                    boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${toneColor} 36%, transparent), 0 0 24px -6px color-mix(in oklab, ${toneColor} 50%, transparent)`,
                  }}
                >
                  <Info
                    size={16}
                    strokeWidth={2}
                    style={{ color: toneColor }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-mono mb-0.5">
                    Подсказка
                  </p>
                  <h3 className="font-display text-[17px] font-extrabold text-[var(--text-primary)] tracking-tight leading-tight">
                    {title}
                  </h3>
                </div>
                <button
                  type="button"
                  aria-label="Закрыть"
                  onClick={handleClose}
                  className="h-7 w-7 rounded-full lg-pill flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition active:scale-90 flex-shrink-0"
                >
                  <X size={13} strokeWidth={2} />
                </button>
              </div>

              {/* Body — скроллится если контент длиннее экрана */}
              <div className="px-5 pb-5 overflow-y-auto flex-1 min-h-0">
                <div className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
                  {body}
                </div>
                {bullets && bullets.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {bullets.map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2.5 text-[12px] text-[var(--text-secondary)] leading-snug"
                      >
                        <span
                          aria-hidden
                          className="mt-1.5 h-1 w-1 rounded-full flex-shrink-0"
                          style={{ background: toneColor }}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Footer gradient hint */}
              <div
                className="h-[3px] w-full"
                style={{
                  background: `linear-gradient(90deg, transparent 0%, ${toneColor} 50%, transparent 100%)`,
                  opacity: 0.5,
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
      )}
    </>
  );
}
